import React, {useRef, useState, useMemo, useEffect} from "react";
import PropTypes from "prop-types";
import { makeStyles } from '@mui/styles';
import clsx from "clsx";
import SMCField from "../SMCField";
import SMCIcon from "../SMCIcon";
import { useSMCDisabledContext } from "../SMCDisabledContext";
import { hasValue } from "../../../utils/utils";

/**
 *
 * @param {object} params
 * @param {boolean} params.disabled
 * @param {boolean} params.readOnly
 * @param {(string|number)} params.value
 * @param {function} params.onChange
 * @param {string} params.className
 * @param {*} params.label
 * @param {('start'|'end')} params.labelPosition
 * @param {(string|number)} params.labelWidth
 * @param {string} params.tooltip
 * @param {boolean} params.inline
 * @param {boolean} params.noAutoComplete
 * @param {object} params.style
 * @param {string} params.placeholder
 * @param {(string|number)} params.height
 * @param {*} params.innerRef
 * @param {(string|number)} params.width
 * @param {string} params.type
 * @param {boolean} params.allowClearAll
 * @param {function} params.onClearAll
 * @param {string} params.suffixIcon
 * @param {function} params.suffixIconOnClick
 * @param {boolean} params.required
 * @param {boolean} params.ignoreDisabledContext
 * @param {string} params.regexoEx
 * @param {function} params.validationFunc
 * @param {string}  params.validationMsg
 * @param {function} params.validationCallback
 */
function SMCTextInput({
  disabled,
  readOnly,
  value,
  inline,
  onChange,
  className,
  label,
  labelPosition,
  labelWidth,
  tooltip,
  style,
  noAutoComplete,
  placeholder,
  width,
  height,
  type = "text",
  innerRef,
  allowClearAll,
  onClearAll,
  suffixIcon,
  suffixIconOnClick,
  required,
  ignoreDisabledContext,
  validationFunc,
  validationMsg,
  validationCallback,
  regexoEx,
  vertical,
  wrapperClassName,
  isValid,
  ...rest
}) {
  const classes = useStyles();
  const ref = useRef();
  const disabledContext = useSMCDisabledContext();

  const _disabled = useMemo(() => {
    if (disabledContext && !ignoreDisabledContext) {
      if (disabledContext.forceDisable) {
        return true;
      }
      if (disabledContext.forceEnable) {
        return false;
      }
    }
    if (hasValue(disabled)) {
      return disabled;
    }
    return undefined;
  }, [disabled, disabledContext, ignoreDisabledContext]);

  let displayValue = value;
  // If value is null, treat as empty string (if value is undefined, input is uncontrolled)
  if (value === null) {
    displayValue = "";
  }

  // If type is password, and input is readOnly, replace string with asterisks
  if (displayValue && readOnly && type === "password") {
    displayValue = Array(displayValue.length + 1).join("*");
  }

  const _ref = innerRef || ref;
  const [showClearAll, setShowClearAll] = useState(
    allowClearAll && _ref.current && _ref.current.value !== ""
  );
  const [valid, setValid] = useState(true);

  useEffect(()=> {
    if (isValid !== undefined) {
      setValid(isValid)
    }
  }, [isValid])

  const _onChange = (e) => {
    setShowClearAll(allowClearAll && e.target.value !== "");
    onChange && onChange(e);
    const validationResult = checkRegex(e.target.value) && checkValidationFunc(e.target.value);
    setValid(validationResult);

    if (validationCallback) {
      validationCallback(validationResult)
    }
  };

  const checkRegex = (txt) => {
    if (!regexoEx) return true;
    let re = new RegExp(regexoEx);
    return re.test(txt);
  };

  const checkValidationFunc = (txt) => {
    return !validationFunc || validationFunc(txt)
  }

  const _onClearAll = (e) => {
    // If passed onClearAll, just call that function
    if (onClearAll) {
      onClearAll(e);
    } else {
      // If uncontrolled, set value directly
      if (typeof value === "undefined") {
        _ref.current.value = "";
      }
      const event = Object.create(e);
      event.target = _ref.current;
      event.currentTarget = _ref.current;
      const originalInputValue = _ref.current.value;
      // change input value cause e.target.value should be '' when clear input
      _ref.current.value = "";
      _onChange(event);
      // reset input value
      _ref.current.value = originalInputValue;
    }
  };

  let suffixIconElem = null;
  if (showClearAll) {
    suffixIconElem = (
      <SMCIcon
        icon="clear"
        onClick={_onClearAll}
        className={classes.suffixIcon}
      />
    );
  } else if (suffixIcon) {
    suffixIconElem = (
      <SMCIcon
        icon={suffixIcon}
        onClick={suffixIconOnClick}
        className={classes.suffixIcon}
      />
    );
  }

  /* If input is read-only, disaplay it with a span */
  const inputElem = (
    <div className={classes.root}>
      {readOnly && (
        <span
          style={{
            width: width,
            height: height,
            paddingRight: suffixIconElem ? "24px" : undefined,
            ...style,
          }}
          disabled={_disabled || readOnly}
          readOnly={_disabled || readOnly}
          className={clsx(
            className,
            classes.input,
            _disabled && classes.disabled,
            readOnly && classes.readOnly
          )}
          placeholder={placeholder}
          ref={_ref}
        >
          {displayValue}
        </span>
      )}
      {!readOnly && (
        <div>
          <input
            style={{
              width: width,
              height: height,
              paddingRight: suffixIconElem ? "24px" : undefined,
              ...style,
              display: "block",
            }}
            type={type}
            value={displayValue}
            disabled={_disabled || readOnly}
            readOnly={_disabled || readOnly}
            className={clsx(
              className,
              classes.input,
              "defaultInput",
              _disabled && classes.disabled,
              readOnly && classes.readOnly
            )}
            onChange={_onChange}
            autoComplete={noAutoComplete ? "off" : "on"}
            placeholder={placeholder}
            ref={_ref}
            {...rest}
          />
          {!valid && (regexoEx || validationFunc) && (
            <span className={clsx(classes.bar, classes.error)}>
              {validationMsg || "Invalid input"}
            </span>
          )}
        </div>
      )}
      {suffixIconElem}
    </div>
  );

  return (
    <SMCField
      inline={inline}
      label={label}
      labelPosition={labelPosition}
      labelWidth={labelWidth}
      tooltip={tooltip}
      required={required}
      vertical={vertical}
      className={wrapperClassName}
    >
      {inputElem}
    </SMCField>
  );
}

const useStyles = makeStyles({
  root: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
  },
  input: {
    boxSizing: "border-box",
    backgroundColor: "#fff",
    color: "#333",
    fontFamily: "Open Sans",
    border: "1px solid #d2d5d9",
    paddingRight: "5px",
    marginRight: "5px",
    "&::placeholder": {
      font: "italic 13px Open Sans",
      color: "#C6C9CF",
    },
    "&:focus": {
      border: "1px solid #4fb4f1",
    },
  },
  disabled: {
    backgroundColor: "#EBEBE4",
    "&:focus": {
      border: "1px solid #d2d5d9",
    },
  },
  readOnly: {
    border: "none",
    background: "none",
    // color: '#8f8f8f'
  },
  suffixIcon: {
    position: "relative",
    right: "27px",
    display: "inline-block",
    marginRight: "-23px",
  },
  bar: {},
  error: {
    fontSize: "8pt",
    color: "red",
  },
});

SMCTextInput.propTypes = {
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  className: PropTypes.string,
  label: PropTypes.any,
  labelPosition: PropTypes.oneOf(["start", "end"]),
  labelWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tooltip: PropTypes.node,
  inline: PropTypes.bool,
  noAutoComplete: PropTypes.bool,
  style: PropTypes.object,
  placeholder: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  type: PropTypes.string,
  innerRef: PropTypes.any,
  allowClearAll: PropTypes.bool,
  onClearAll: PropTypes.func,
  suffixIcon: PropTypes.string,
  required: PropTypes.bool,
  ignoreDisabledContext: PropTypes.bool,
  regexoEx: PropTypes.string,
  validationFunc : PropTypes.func,
  validationMsg: PropTypes.string,
  validationCallback: PropTypes.func
};

export default React.memo(SMCTextInput);

